import React, { useState } from "react";
import { useProperty } from "@/context/PropertyContext";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Building2, Plus, Pencil, Trash2, DoorClosed } from "lucide-react";
import { eur } from "@/lib/format";
import { toast } from "sonner";

const empty = {
  nombre: "", direccion: "", arrendador_nombre: "", arrendador_dni: "",
  arrendador_direccion: "", iban: "", habitaciones: [], gasto_mensual_default: 60,
  fianza_default: 400, coste_llave: 30, irpf_modo: 50, plantilla_contrato: "",
};

export default function Properties() {
  const { properties, reload } = useProperty();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);

  const openNew = () => { setForm(empty); setEditId(null); setOpen(true); };
  const openEdit = (p) => {
    setForm({ ...empty, ...p, habitaciones: p.habitaciones || [] });
    setEditId(p.id);
    setOpen(true);
  };

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const addRoom = () =>
    setForm((f) => ({ ...f, habitaciones: [...f.habitaciones, { numero: "", precio: 0, descripcion: "" }] }));
  const setRoom = (i, k, v) =>
    setForm((f) => {
      const h = [...f.habitaciones];
      h[i] = { ...h[i], [k]: v };
      return { ...f, habitaciones: h };
    });
  const delRoom = (i) =>
    setForm((f) => ({ ...f, habitaciones: f.habitaciones.filter((_, idx) => idx !== i) }));

  const save = async () => {
    if (!form.nombre.trim()) return toast.error("El nombre del piso es obligatorio");
    if ([form.gasto_mensual_default, form.fianza_default, form.coste_llave].some((v) => Number(v) < 0)) {
      return toast.error("Los importes no pueden ser negativos");
    }
    if (form.habitaciones.some((h) => Number(h.precio) < 0)) {
      return toast.error("El precio de una habitación no puede ser negativo");
    }
    const payload = {
      ...form,
      gasto_mensual_default: Number(form.gasto_mensual_default) || 0,
      fianza_default: Number(form.fianza_default) || 0,
      coste_llave: Number(form.coste_llave) || 0,
      irpf_modo: Number(form.irpf_modo) || 50,
      habitaciones: form.habitaciones.map((h) => ({
        numero: String(h.numero), precio: Number(h.precio) || 0, descripcion: h.descripcion || "",
      })),
    };
    try {
      if (editId) await api.put(`/properties/${editId}`, payload);
      else await api.post("/properties", payload);
      toast.success(editId ? "Piso actualizado" : "Piso creado");
      setOpen(false);
      reload();
    } catch (e) {
      toast.error("Error al guardar el piso");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar este piso y TODOS sus datos (inquilinos, contratos, pagos, gastos)?")) return;
    await api.delete(`/properties/${id}`);
    toast.success("Piso eliminado");
    reload();
  };

  return (
    <div data-testid="properties-page">
      <PageHeader title="Pisos" subtitle="Tus propiedades">
        <Button onClick={openNew} data-testid="add-property-button">
          <Plus className="h-4 w-4 mr-1.5" /> Nuevo piso
        </Button>
      </PageHeader>

      {properties.length === 0 ? (
        <div className="data-card p-10 text-center text-muted-foreground">
          Aún no tienes pisos. Crea el primero para empezar a gestionar inquilinos y contratos.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {properties.map((p) => (
            <div key={p.id} className="data-card p-5" data-testid={`property-card-${p.id}`}>
              <div className="flex items-start justify-between">
                <div className="h-10 w-10 rounded-md bg-primary/10 flex items-center justify-center">
                  <Building2 className="h-5 w-5 text-primary" />
                </div>
                <div className="flex gap-1">
                  <button onClick={() => openEdit(p)} className="p-1.5 text-muted-foreground hover:text-primary" data-testid={`edit-property-${p.id}`}>
                    <Pencil className="h-4 w-4" />
                  </button>
                  {user?.role === "admin" && (
                    <button onClick={() => remove(p.id)} className="p-1.5 text-muted-foreground hover:text-destructive">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
              <h3 className="font-display font-medium text-lg mt-3">{p.nombre}</h3>
              <p className="text-sm text-muted-foreground">{p.direccion || "Sin dirección"}</p>
              <div className="flex items-center gap-4 mt-4 text-sm">
                <span className="flex items-center gap-1.5 text-muted-foreground">
                  <DoorClosed className="h-4 w-4" /> {(p.habitaciones || []).length} hab.
                </span>
                <span className="font-mono text-muted-foreground">Gastos: {eur(p.gasto_mensual_default)}/mes</span>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">{editId ? "Editar piso" : "Nuevo piso"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label>Nombre del piso *</Label>
                <Input value={form.nombre} onChange={(e) => setField("nombre", e.target.value)} data-testid="property-nombre-input" placeholder="Piso Guridi" />
              </div>
              <div>
                <Label>Dirección</Label>
                <Input value={form.direccion} onChange={(e) => setField("direccion", e.target.value)} />
              </div>
            </div>

            <p className="overline pt-2">Datos del arrendador (para contratos)</p>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label>Nombre arrendador</Label>
                <Input value={form.arrendador_nombre} onChange={(e) => setField("arrendador_nombre", e.target.value)} />
              </div>
              <div>
                <Label>DNI arrendador</Label>
                <Input value={form.arrendador_dni} onChange={(e) => setField("arrendador_dni", e.target.value)} />
              </div>
              <div>
                <Label>Dirección arrendador</Label>
                <Input value={form.arrendador_direccion} onChange={(e) => setField("arrendador_direccion", e.target.value)} />
              </div>
              <div>
                <Label>IBAN</Label>
                <Input value={form.iban} onChange={(e) => setField("iban", e.target.value)} />
              </div>
            </div>

            <p className="overline pt-2">Condiciones por defecto</p>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Gastos/mes (€)</Label>
                <Input type="number" min="0" value={form.gasto_mensual_default} onChange={(e) => setField("gasto_mensual_default", e.target.value)} />
                {Number(form.gasto_mensual_default) === 0 && (
                  <p className="text-xs text-warning mt-1">
                    Gastos a 0€: recuerda que este importe también figura en la plantilla del contrato.
                  </p>
                )}
              </div>
              <div>
                <Label>Fianza (€)</Label>
                <Input type="number" min="0" value={form.fianza_default} onChange={(e) => setField("fianza_default", e.target.value)} />
                {Number(form.fianza_default) === 0 && (
                  <p className="text-xs text-warning mt-1">
                    Fianza a 0€: recuerda que este importe también figura en la plantilla del contrato.
                  </p>
                )}
              </div>
              <div>
                <Label>Coste llave (€)</Label>
                <Input type="number" min="0" value={form.coste_llave} onChange={(e) => setField("coste_llave", e.target.value)} />
              </div>
            </div>

            <div className="flex items-center justify-between pt-2">
              <p className="overline">Habitaciones</p>
              <Button variant="outline" size="sm" onClick={addRoom} data-testid="add-room-button">
                <Plus className="h-3.5 w-3.5 mr-1" /> Habitación
              </Button>
            </div>
            <div className="space-y-2">
              {form.habitaciones.map((h, i) => (
                <div key={i} className="flex gap-2 items-center">
                  <Input className="w-20" placeholder="Nº" value={h.numero} onChange={(e) => setRoom(i, "numero", e.target.value)} />
                  <Input className="w-28" type="number" min="0" placeholder="Precio €" value={h.precio} onChange={(e) => setRoom(i, "precio", e.target.value)} />
                  <Input className="flex-1" placeholder="Descripción" value={h.descripcion} onChange={(e) => setRoom(i, "descripcion", e.target.value)} />
                  <button onClick={() => delRoom(i)} className="text-muted-foreground hover:text-destructive p-1">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={save} data-testid="save-property-button">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
